import React, { useMemo } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStepForward } from '@fortawesome/free-solid-svg-icons';
import { StepType } from '@ibe/services';

export interface ImageProgressbarStep {
  description: string;
  selected?: boolean;
  image: IconProp;
  stepType?: StepType;
  optional?: boolean;
  skipped?: boolean;
}
export interface ImageProgressbarProps {
  steps: ImageProgressbarStep[];
  onClick: (stepIndex: number, description: string) => void;
  showNotActiveIcon?: boolean;
  activeStep?: StepType;
  showDescriptionOnlyForActiveTab?: boolean;
}

const ImageProgressBar = (props: ImageProgressbarProps): JSX.Element => {
  const { steps, onClick, showNotActiveIcon, activeStep, showDescriptionOnlyForActiveTab } = props;
  const activeStepIndex = useMemo(() => {
    return steps.findIndex(step => (!!activeStep && activeStep === step.stepType) || step.selected);
  }, [steps, activeStep]);

  function getDescription(step: ImageProgressbarStep, index: number): string {
    if (showDescriptionOnlyForActiveTab) {
      return index === activeStepIndex ? step.description : '';
    }
    return step.description;
  }

  return (
    <div
      className={`iso__imageProgressBar ${
        activeStepIndex === steps.length - 1 ? 'last-step-active' : ''
      }`}
    >
      {steps.map(
        (step: ImageProgressbarStep, index: number, arr: ImageProgressbarStep[]): JSX.Element => {
          return (
            <div
              onClick={
                index < activeStepIndex ||
                (activeStepIndex === index - 1 && !!arr[activeStepIndex]?.optional)
                  ? (): void => onClick(index, step.description)
                  : undefined
              }
              key={step.description}
              className={classNames({
                iso__imageProgressBar__stepperItem: true,
                'iso__imageProgressBar__stepperItem--active': index === activeStepIndex,
                'iso__imageProgressBar__stepperItem--completed': index < activeStepIndex,
                'iso__imageProgressBar__stepperItem--skipped': !!step.skipped
              })}
            >
              <div
                className={classNames({
                  iso__imageProgressBar__stepperItem__stepImage: true,
                  'iso__imageProgressBar__stepperItem__stepImage--active':
                    index === activeStepIndex,
                  'iso__imageProgressBar__stepperItem__stepImage--completed':
                    index < activeStepIndex,
                  'iso__imageProgressBar__stepperItem__stepImage--skipped': !!step.skipped
                })}
              >
                {!!step.skipped && index < activeStepIndex && (
                  <FontAwesomeIcon icon={faStepForward} />
                )}

                {!step.skipped && index < activeStepIndex && <FontAwesomeIcon icon={faCheck} />}

                {((activeStepIndex === index - 1 && !!arr[activeStepIndex]?.optional) ||
                  ((index === activeStepIndex || showNotActiveIcon) &&
                    index >= activeStepIndex)) && <FontAwesomeIcon icon={step.image} />}
              </div>
              <div
                className={classNames({
                  iso__imageProgressBar__stepperItem__stepName: true,
                  'iso__imageProgressBar__stepperItem__stepName--active': index === activeStepIndex,
                  'iso__imageProgressBar__stepperItem__stepName--completed':
                    index < activeStepIndex,
                  'iso__imageProgressBar__stepperItem__stepName--skipped': !!step.skipped
                })}
              >
                {getDescription(step, index)}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
};

export default ImageProgressBar;
